import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backend';

export const useAudioAPI = (getAuthHeaders, getToken, getShareToken) => {
  const API = `${BACKEND_URL}/api`;

  const [files, setFiles] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(false);

  // Use ref to avoid stale closure issues
  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;

  // Live client-only optimistic upload rows, keyed by tempId. The server never
  // returns these (they have `temp-...` ids it's never heard of), so a naive
  // setFiles(response.data) in fetchFiles would wipe any CONCURRENT upload's
  // row mid-flight — its progress bar would blink out until its own next fetch.
  // We track them here and re-merge on every fetchFiles so sibling uploads
  // survive each other's list refreshes.
  const optimisticRowsRef = useRef({});

  const fetchFiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/files`, getAuthHeadersRef.current());
      const serverFiles = response.data || [];
      // Re-add any still-in-flight optimistic rows the server doesn't know about,
      // keeping them at the top (most-recent-first, matching addOptimisticFile).
      const stillPending = Object.values(optimisticRowsRef.current)
        .filter((o) => !serverFiles.some((s) => s.id === o.id));
      setFiles(stillPending.length ? [...stillPending, ...serverFiles] : serverFiles);
      return serverFiles;
    } catch (error) {
      console.error('Error fetching files:', error);
      return [];
    }
  }, [API]);

  const uploadFile = useCallback(async (file, onProgress, metadata = null) => {
    try {
      // Upload via backend proxy to avoid CORS issues with S3.
      // `metadata` (optional): { session_type, client_name, session_date } —
      // when provided the backend composes the canonical title
      // "{session_type} - {client_name} | DD.MM.YYYY"; otherwise falls back
      // to the stripped filename.
      const formData = new FormData();
      formData.append('file', file);
      formData.append('duration', 0);

      const params = {};
      if (metadata?.session_type) params.session_type = metadata.session_type;
      if (metadata?.client_name) params.client_name = metadata.client_name;
      if (metadata?.session_date) params.session_date = metadata.session_date;
      if (metadata?.language) params.language = metadata.language;
      // Доступ к записи задаётся прямо при загрузке — без этих полей выбор
      // клиента и аудитории в форме никуда бы не доехал.
      if (metadata?.visibility) params.visibility = metadata.visibility;
      if (metadata?.client_profile_id) params.client_profile_id = metadata.client_profile_id;
      if (metadata?.client_profile_name) params.client_profile_name = metadata.client_profile_name;
      if (metadata?.therapy_session_id) params.therapy_session_id = metadata.therapy_session_id;

      const response = await axios.post(`${API}/files/upload-s3`, formData, {
        ...getAuthHeadersRef.current(),
        headers: {
          ...getAuthHeadersRef.current().headers,
          'Content-Type': 'multipart/form-data'
        },
        params,
        onUploadProgress: onProgress
      });

      console.log('✅ File uploaded to Selectel S3:', response.data.id);

      // Refresh file list
      await fetchFiles();

      return { id: response.data.id };

    } catch (error) {
      console.error('❌ Upload error:', error);
      throw error;
    }
  }, [API, fetchFiles]);

  const downloadFile = useCallback(async (fileId, version = '') => {
    // Skip temporary files that haven't been uploaded yet
    if (fileId.startsWith('temp-')) {
      return null;
    }

    const token = getToken ? getToken() : null;
    const shareToken = getShareToken ? getShareToken() : null;
    // `version` is a cache-busting token derived from file metadata (e.g.
    // peaks_duration). When the backend re-encodes or regenerates peaks, the
    // version changes, the URL changes, and the browser is forced to fetch
    // fresh bytes instead of serving a stale <audio> media cache.
    const vq = version ? `&v=${encodeURIComponent(version)}` : '';
    let streamUrl;
    if (token) {
      streamUrl = `${BACKEND_URL}/api/files/${fileId}/stream?token=${encodeURIComponent(token)}${vq}`;
    } else if (shareToken) {
      streamUrl = `${BACKEND_URL}/api/files/${fileId}/stream?share_token=${encodeURIComponent(shareToken)}${vq}`;
    } else {
      streamUrl = `${BACKEND_URL}/api/files/${fileId}/stream${version ? `?v=${encodeURIComponent(version)}` : ''}`;
    }
    return { type: 'url', url: streamUrl };
  }, [BACKEND_URL, getToken, getShareToken]);

  const uploadDualFile = useCallback(async (expertFile, clientFile, onProgress, metadata = null) => {
    // Dual-track upload — two synchronized MP3 files. Backend mixes them
    // into one playable file via ffmpeg amix, keeps originals for
    // per-track transcription, and returns 202 as soon as both uploads
    // complete (same fire-and-forget pattern as single-file upload).
    try {
      const formData = new FormData();
      formData.append('file_expert', expertFile);
      formData.append('file_client', clientFile);
      formData.append('duration', 0);

      const params = {};
      if (metadata?.session_type) params.session_type = metadata.session_type;
      if (metadata?.client_name) params.client_name = metadata.client_name;
      if (metadata?.session_date) params.session_date = metadata.session_date;
      if (metadata?.language) params.language = metadata.language;
      // Доступ к записи задаётся прямо при загрузке — без этих полей выбор
      // клиента и аудитории в форме никуда бы не доехал.
      if (metadata?.visibility) params.visibility = metadata.visibility;
      if (metadata?.client_profile_id) params.client_profile_id = metadata.client_profile_id;
      if (metadata?.client_profile_name) params.client_profile_name = metadata.client_profile_name;
      if (metadata?.therapy_session_id) params.therapy_session_id = metadata.therapy_session_id;

      const response = await axios.post(`${API}/files/upload-s3-dual`, formData, {
        ...getAuthHeadersRef.current(),
        headers: {
          ...getAuthHeadersRef.current().headers,
          'Content-Type': 'multipart/form-data',
        },
        params,
        onUploadProgress: onProgress,
      });
      console.log('✅ Dual file uploaded to Selectel S3:', response.data.id);
      await fetchFiles();
      return { id: response.data.id, dual_track: true };
    } catch (error) {
      console.error('❌ Dual upload error:', error);
      throw error;
    }
  }, [API, fetchFiles]);

  // Люди из гипнозио для привязки записи к клиенту. Ходим не в гипнозио
  // напрямую, а в свой бэкенд: общий секрет остаётся на сервере, а список
  // видят только эксперты.
  // Принимает либо строку поиска, либо набор параметров целиком
  // ({ q, therapy, has_journal, via, min_level }) — окно привязки фильтрует,
  // форме загрузки хватает строки.
  const searchClients = useCallback(async (queryOrParams = '') => {
    const params = typeof queryOrParams === 'string'
      ? (queryOrParams ? { q: queryOrParams } : {})
      : Object.fromEntries(Object.entries(queryOrParams || {}).filter(([, v]) => v));
    try {
      const response = await axios.get(`${API}/clients`, {
        ...getAuthHeadersRef.current(),
        params,
      });
      return response.data?.clients || [];
    } catch (error) {
      // null, а не пустой список: «никого не нашли» и «гипнозио не отвечает» —
      // разные вещи, и форма должна сказать об этом по-разному.
      console.error('Error searching clients:', error);
      return null;
    }
  }, [API]);

  const fetchClientSessions = useCallback(async (profileId) => {
    if (!profileId) return [];
    try {
      const response = await axios.get(
        `${API}/clients/${profileId}/sessions`,
        getAuthHeadersRef.current()
      );
      return response.data?.sessions || [];
    } catch (error) {
      console.error('Error fetching client sessions:', error);
      return [];
    }
  }, [API]);

  // Сменить аудиторию записи или привязку к клиенту после загрузки.
  const updateFileAccess = useCallback(async (fileId, patch) => {
    const response = await axios.patch(
      `${API}/files/${fileId}/access`,
      patch,
      getAuthHeadersRef.current()
    );
    await fetchFiles();
    return response.data;
  }, [API, fetchFiles]);

  const deleteFile = useCallback(async (fileId) => {
    await axios.delete(`${API}/files/${fileId}`, getAuthHeadersRef.current());
    await fetchFiles();
  }, [API, fetchFiles]);

  const updateFileTitle = useCallback(async (fileId, title) => {
    await axios.put(`${API}/files/${fileId}/title`, { title }, getAuthHeadersRef.current());
    await fetchFiles();
  }, [API, fetchFiles]);

  // Lazy-loaded full file details (peaks + transcript). The list endpoint
  // /api/files intentionally strips heavy fields (waveform_peaks, transcript*)
  // to keep the initial page load light — this pulls them on demand for the
  // currently-opened track.
  const fetchFileDetails = useCallback(async (fileId) => {
    if (!fileId || fileId.startsWith('temp-')) return null;
    const response = await axios.get(
      `${API}/files/${fileId}`,
      getAuthHeadersRef.current()
    );
    return response.data;
  }, [API]);

  const startTranscription = useCallback(async (fileId) => {
    const response = await axios.post(
      `${API}/files/${fileId}/transcribe`,
      null,
      getAuthHeadersRef.current()
    );
    return response.data;
  }, [API]);

  const fetchTranscript = useCallback(async (fileId) => {
    const response = await axios.get(
      `${API}/files/${fileId}/transcript`,
      getAuthHeadersRef.current()
    );
    return response.data;
  }, [API]);

  const updateSpeakerLabels = useCallback(async (fileId, labels) => {
    const response = await axios.patch(
      `${API}/files/${fileId}/transcript/speakers`,
      labels,
      getAuthHeadersRef.current()
    );
    return response.data;
  }, [API]);

  // Fetch single marker's comments (kept for individual use)
  const fetchCommentsForMarker = useCallback(async (markerId) => {
    try {
      const response = await axios.get(`${API}/comments/${markerId}`, getAuthHeadersRef.current());
      return { markerId, comments: response.data };
    } catch (error) {
      console.error('Error fetching comments for marker:', markerId, error);
      return { markerId, comments: [] };
    }
  }, [API]);

  // Both reads (markers + comments-by-file) accept a `?share_token=` query so
  // a guest viewing a track via a share link can see existing comments. We
  // attach the token automatically when there's no real JWT.
  //
  // NOTE: don't use `headers.Authorization` to detect JWT presence — for an
  // unauthenticated guest, AuthContext.getAuthHeaders() still composes
  // `Bearer ${token}`, which stringifies a null token to the literal
  // `"Bearer null"`. That's truthy as a string, but obviously not a JWT, so
  // gating on the header silently dropped share_token for guests and the
  // backend then 401'd. Use getToken() (which returns the raw value) instead.
  const buildReadParams = useCallback(() => {
    const headers = getAuthHeadersRef.current();
    const jwt = getToken ? getToken() : null;
    if (jwt) return headers;
    const shareToken = getShareToken ? getShareToken() : null;
    if (!shareToken) return headers;
    return { ...headers, params: { ...(headers.params || {}), share_token: shareToken } };
  }, [getToken, getShareToken]);

  // Batch fetch all comments for a file in a single request
  const fetchAllCommentsForFile = useCallback(async (audioId) => {
    try {
      const response = await axios.get(`${API}/comments/by-file/${audioId}`, buildReadParams());
      return response.data; // dict: { marker_id: [comments] }
    } catch (error) {
      console.error('Error fetching batch comments for audio:', audioId, error);
      return {};
    }
  }, [API, buildReadParams]);

  const fetchMarkersForAudio = useCallback(async (audioId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/markers/${audioId}`, buildReadParams());
      setMarkers(response.data);

      // Use batch endpoint to fetch all comments in ONE request
      if (response.data.length > 0) {
        const batchComments = await fetchAllCommentsForFile(audioId);
        setComments(batchComments);
      } else {
        setComments({});
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching markers:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [API, fetchAllCommentsForFile]);

  const createMarkerWithComment = useCallback(async (audioId, timestamp, text) => {
    // Create marker
    const markerResponse = await axios.post(`${API}/markers`, {
      audio_id: audioId,
      timestamp
    }, getAuthHeadersRef.current());

    const marker = markerResponse.data;

    // Create comment
    const commentResponse = await axios.post(`${API}/comments`, {
      marker_id: marker.id,
      text
    }, getAuthHeadersRef.current());

    const comment = commentResponse.data;

    // Optimistic update - add marker and comment locally without refetching
    setMarkers(prev => [...prev, marker].sort((a, b) => a.timestamp - b.timestamp));
    setComments(prev => ({
      ...prev,
      [marker.id]: [comment]
    }));
    
    return marker;
  }, [API]);

  const deleteMarker = useCallback(async (markerId, audioId) => {
    await axios.delete(`${API}/markers/${markerId}`, getAuthHeadersRef.current());
    if (audioId) {
      await fetchMarkersForAudio(audioId);
    }
  }, [API, fetchMarkersForAudio]);

  const updateComment = useCallback(async (commentId, text) => {
    await axios.put(`${API}/comments/${commentId}`, { text }, getAuthHeadersRef.current());
  }, [API]);

  const deleteComment = useCallback((commentId, markerId) => {
    // Optimistic update FIRST (instant UI response)
    const markerComments = comments[markerId] || [];
    const remainingComments = markerComments.filter(c => c.id !== commentId);
    
    if (remainingComments.length === 0) {
      // Last comment - remove marker too
      setMarkers(prev => prev.filter(m => m.id !== markerId));
      setComments(prev => {
        const newComments = { ...prev };
        delete newComments[markerId];
        return newComments;
      });
    } else {
      // Just update comments
      setComments(prev => ({
        ...prev,
        [markerId]: remainingComments
      }));
    }

    // Fire and forget - delete in background
    axios.delete(`${API}/comments/${commentId}`, getAuthHeadersRef.current())
      .catch(err => console.error('Delete comment error:', err));
  }, [API, comments]);

  const resetMarkersAndComments = useCallback(() => {
    setMarkers([]);
    setComments({});
  }, []);

  // Optimistic file operations. Each also maintains optimisticRowsRef so
  // fetchFiles can re-merge live rows (see fetchFiles above).
  const addOptimisticFile = useCallback((file) => {
    optimisticRowsRef.current[file.id] = file;
    setFiles(prev => [file, ...prev]);
  }, []);

  const replaceOptimisticFile = useCallback((tempId, realFile) => {
    delete optimisticRowsRef.current[tempId];
    setFiles(prev => prev.map(f => f.id === tempId ? realFile : f));
  }, []);

  const removeOptimisticFile = useCallback((tempId) => {
    delete optimisticRowsRef.current[tempId];
    setFiles(prev => prev.filter(f => f.id !== tempId));
  }, []);

  // Merge a partial update into ONE file in the list by id. Used by the
  // transcription + processing pollers so a row's status/badge updates even
  // when that file isn't the currently-open one. No-op if the id isn't present.
  //
  // Short-circuits when the patch changes nothing: the pollers re-issue the
  // same status every few seconds while a job stays running/processing, and
  // without this guard each tick would allocate a new array + object and
  // re-render the whole list for identical data. If every patched field already
  // matches, return `prev` so React bails out of the re-render.
  const patchFileInList = useCallback((fileId, patch) => {
    setFiles(prev => {
      const target = prev.find(f => f.id === fileId);
      if (!target) return prev;
      const changed = Object.keys(patch).some(k => target[k] !== patch[k]);
      if (!changed) return prev;
      return prev.map(f => f.id === fileId ? { ...f, ...patch } : f);
    });
  }, []);

  return {
    // State
    files,
    markers,
    comments,
    loading,
    // File operations
    fetchFiles,
    uploadFile,
    downloadFile,
    fetchFileDetails,
    uploadDualFile,
    deleteFile,
    updateFileTitle,
    updateFileAccess,
    searchClients,
    fetchClientSessions,
    startTranscription,
    fetchTranscript,
    updateSpeakerLabels,
    // Optimistic file operations
    addOptimisticFile,
    replaceOptimisticFile,
    removeOptimisticFile,
    patchFileInList,
    // Marker operations
    fetchMarkersForAudio,
    createMarkerWithComment,
    deleteMarker,
    // Comment operations
    fetchAllCommentsForFile,
    updateComment,
    deleteComment,
    // Utils
    resetMarkersAndComments
  };
};
